import React, { useState } from "react";
import axios from "axios";
import { Send, Check, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  { value: "individual", label: "Individual" },
  { value: "institution", label: "Institution" },
  { value: "collaboration", label: "Collaboration" },
  { value: "speaking", label: "Speaking" },
  { value: "media", label: "Media" },
];

const ContactForm = ({ compact = false, onSuccess }) => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: "", inquiry_type: "individual", message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus({ state: "error", msg: "Please fill in name, email and message." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res?.status === 201 || res?.status === 200) {
        setStatus({ state: "success", msg: "Message sent — I'll be in touch shortly." });
        setForm({ name: "", email: "", phone: "", subject: "", inquiry_type: "individual", message: "" });
        if (onSuccess) onSuccess();
      } else {
        setStatus({ state: "error", msg: "Something went wrong. Please try again." });
      }
    } catch (err) {
      setStatus({
        state: "error",
        msg: err?.response?.data?.detail
          ? String(err.response.data.detail)
          : "Network error. Please try again or email me directly.",
      });
    }
  };

  return (
    <form data-testid="contact-form" onSubmit={onSubmit} noValidate>
      {!compact && (
        <div className="font-display font-bold text-ink text-2xl mb-6">Send a Message</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input data-testid="contact-input-name" name="name" value={form.name} onChange={onChange} required className="form-input" placeholder="Full Name *" />
        <input data-testid="contact-input-email" name="email" type="email" value={form.email} onChange={onChange} required className="form-input" placeholder="Email Address *" />
        <input data-testid="contact-input-phone" name="phone" value={form.phone} onChange={onChange} className="form-input" placeholder="Phone (optional)" />
        <select data-testid="contact-input-inquiry" name="inquiry_type" value={form.inquiry_type} onChange={onChange} className="form-input">
          {INQUIRY_TYPES.map((it) => (
            <option key={it.value} value={it.value}>
              I'm {it.label === "Media" || it.label === "Speaking" ? "with " + it.label : "an " + it.label}
            </option>
          ))}
        </select>
      </div>
      <input data-testid="contact-input-subject" name="subject" value={form.subject} onChange={onChange} className="form-input mt-4" placeholder="Subject" />
      <textarea data-testid="contact-input-message" name="message" value={form.message} onChange={onChange} required className="form-input mt-4" placeholder="Tell me a bit about what you're working on... *" rows={5} />

      <div className="mt-6 flex flex-wrap items-center gap-5 justify-between">
        <button data-testid="contact-submit" type="submit" disabled={status.state === "loading"} className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed">
          {status.state === "loading" ? "Sending..." : "Submit Message"}
          <Send size={14} />
        </button>
        {status.state === "success" && (
          <div data-testid="contact-status-success" className="flex items-center gap-2 text-emerald2">
            <Check size={16} /> <span className="text-sm">{status.msg}</span>
          </div>
        )}
        {status.state === "error" && (
          <div data-testid="contact-status-error" className="flex items-center gap-2 text-terracotta">
            <AlertCircle size={16} /> <span className="text-sm">{status.msg}</span>
          </div>
        )}
      </div>
    </form>
  );
};

export default ContactForm;
